// page-actions/url-state.ts — Persistance de l'état de #actions dans le hash.
//
// Sixième tranche extraite de `renderActionsPage` (point 16, 2026-09-08).
// Format : `#actions?raci=RA&team=1&team_id=12&proj=45&prio=2&view=matrix…`
// Les paramètres absents prennent leur valeur par défaut.
//
// Deux couches :
// - `parseActionsUrl` / `actionsUrlQuery` : purs, miroirs l'un de l'autre.
//   Ils couvrent les filtres (domaine), le mode d'affichage, les seuils de la
//   matrice ET les deux modes réunion : ceux-ci sont réécrits dans le hash
//   pour survivre à un rechargement ou à un lien copié.
// - `createActionsUrlSync` : glu navigateur, écrit le hash par replaceState
//   (pas d'entrée d'historique par toggle) et relit le localStorage isolé par
//   tenant quand l'URL n'a pas de query.
//
// Le localStorage n'est PAS écrit pendant un mode réunion : ce périmètre est
// transitoire, une visite ultérieure de #actions ne doit pas rouvrir la liste
// filtrée sur la réunion (régression du 2026-09-08, corrigée le même jour).
// Le retour back/forward est géré par le routeur, qui re-dispatche la page et
// rappelle `fromUrl` : ce module n'écoute donc pas `hashchange`.
//
// Globaux ambiants : todayStr, tlsGet, tlsSet, safeWarn.

import {
  defaultActionsFilterState,
  filterStateToParams,
  parseActionsFilterState,
  type ActionsFilterState,
} from '../../domain/actions-filter.js';
import {
  DEFAULT_EISENHOWER_THRESHOLDS,
  type EisenhowerThresholds,
} from '../../domain/eisenhower.js';
import type { BulkBarLink } from './bulk-bar.js';

export type ActionsViewMode = 'list' | 'matrix';

/** Part de l'état de la page qui vit dans l'URL, dans les deux sens. */
export interface ActionsUrlState {
  filters: ActionsFilterState;
  viewMode: ActionsViewMode;
  thresholds: EisenhowerThresholds;
  /** `meeting=<id>` : rattachement à une réunion. L'URL ne connaît que l'id,
   * le libellé et les actions déjà liées arrivent au chargement du mode. */
  link: BulkBarLink | null;
  /** `meeting_view=<id>` : consultation des actions liées, sans bandeau. */
  meetingViewId: number | null;
}

const STORAGE_KEY = 'actions-filters-qs';

function intParam(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  return raw && /^\d+$/.test(raw) ? parseInt(raw) : null;
}

export function parseActionsUrl(qs: string, today: string): ActionsUrlState {
  const params = new URLSearchParams(qs);
  const filters = parseActionsFilterState(qs, today);

  const impP = params.get('imp');
  const urgP = params.get('urg');
  const thresholds: EisenhowerThresholds = {
    importanceMax:
      impP != null && /^[0-4]$/.test(impP)
        ? parseInt(impP)
        : DEFAULT_EISENHOWER_THRESHOLDS.importanceMax,
    urgencyDays:
      urgP != null && /^\d{1,3}$/.test(urgP)
        ? Math.min(365, parseInt(urgP))
        : DEFAULT_EISENHOWER_THRESHOLDS.urgencyDays,
  };

  const linkMeetingId = intParam(params, 'meeting');
  // En mode rattachement, les terminées restent visibles : une action
  // principale clôturée doit rester décochable, sinon elle « revient » après
  // mise à jour (bug #5). Le fetch include_completed est forcé côté index.ts.
  if (linkMeetingId != null) filters.fHideDone = false;

  return {
    filters,
    viewMode: params.get('view') === 'matrix' ? 'matrix' : 'list',
    thresholds,
    link: linkMeetingId != null ? { meetingId: linkMeetingId, status: 'loading' } : null,
    meetingViewId: intParam(params, 'meeting_view'),
  };
}

/** Query string (sans `?`) ; vide quand tout est à sa valeur par défaut. */
export function actionsUrlQuery(s: ActionsUrlState): string {
  const params = new URLSearchParams(filterStateToParams(s.filters));
  if (s.viewMode === 'matrix') params.set('view', 'matrix');
  if (s.thresholds.importanceMax !== DEFAULT_EISENHOWER_THRESHOLDS.importanceMax)
    params.set('imp', String(s.thresholds.importanceMax));
  if (s.thresholds.urgencyDays !== DEFAULT_EISENHOWER_THRESHOLDS.urgencyDays)
    params.set('urg', String(s.thresholds.urgencyDays));
  if (s.link) params.set('meeting', String(s.link.meetingId));
  if (s.meetingViewId) params.set('meeting_view', String(s.meetingViewId));
  return params.toString();
}

export function defaultActionsUrlState(): ActionsUrlState {
  return {
    filters: defaultActionsFilterState(),
    viewMode: 'list',
    thresholds: { ...DEFAULT_EISENHOWER_THRESHOLDS },
    link: null,
    meetingViewId: null,
  };
}

/** Un mode réunion est en cours : le périmètre affiché ne se mémorise pas. */
export function inMeetingMode(s: ActionsUrlState): boolean {
  return !!s.link || s.meetingViewId != null;
}

export interface ActionsUrlSync {
  /** État → hash (replaceState) + localStorage hors mode réunion. */
  toUrl: () => void;
  /** Hash (ou localStorage à défaut) → état, en place. */
  fromUrl: () => void;
}

/** Lie l'objet d'état de la page à l'URL. `state` est muté en place : la page
 * en reste propriétaire et le lit après chaque `fromUrl`. */
export function createActionsUrlSync(state: ActionsUrlState): ActionsUrlSync {
  function fromUrl(): void {
    const hash = window.location.hash || '';
    const qIdx = hash.indexOf('?');
    const qs = qIdx >= 0 ? hash.slice(qIdx + 1) : tlsGet(STORAGE_KEY) || '';
    Object.assign(state, qs ? parseActionsUrl(qs, todayStr()) : defaultActionsUrlState());
  }

  function toUrl(): void {
    const qs = actionsUrlQuery(state);
    const newHash = qs ? `#actions?${qs}` : '#actions';
    if (window.location.hash !== newHash) {
      try {
        // replaceState : pas d'entrée d'historique à chaque toggle de filtre.
        history.replaceState(null, '', newHash);
      } catch (e) {
        safeWarn('history.replaceState (filters)', e);
        window.location.hash = newHash.slice(1);
      }
    }
    if (!inMeetingMode(state)) tlsSet(STORAGE_KEY, qs);
  }

  return { toUrl, fromUrl };
}
